import React, { useState } from 'react';
import { ParsedSunoOutput, MusicCritique } from '@/types';
import { CopyButton } from './CopyButton';
import { Tooltip } from './Tooltip';
import { Zap, Code, FileText, Music, Download, Tags, Sparkles, AlertCircle, ExternalLink, Scale, Ban, Pencil, Save, X } from 'lucide-react';
import { useCompare, useCreateCompareSlot } from '@context/CompareContext';
import { PronunciationFixModal } from './PronunciationFixModal';
import { scanForPronunciationIssues, ReplacementResult } from '@/utils/pronunciationFixes';
import { Button, IconButton, Badge, LoadingSpinner, StatusDot, useToast } from './ui';

type EditableOutputField = 'summary' | 'style' | 'excludedStyle' | 'styleBlueprint';

interface OutputSectionProps {
  output: ParsedSunoOutput;
  blueprint?: string | null;
  critique?: MusicCritique | null;
  isSyncing: boolean;
  onSync: () => void;
  onApplyRevision?: () => void;
  onLyricsUpdate?: (updatedLyrics: string) => Promise<void>;
  /** Forgejo #106 -- persists an edit to Narrative Summary / Machine Tags /
   *  Excluded Styles / Production Blueprint back to the DraftVault row.
   *  Throws on failure so the edit UI can keep the editor open. */
  onFieldUpdate?: (field: EditableOutputField, value: string) => Promise<void>;
  /** False until the current output has an actual DraftVault row to save
   *  edits to (auto-save runs once, right after generation) -- edit
   *  affordances are hidden until then so a save can never look like it
   *  worked when there's nothing to persist it to. */
  editingEnabled?: boolean;
  isSunoReady: boolean;
}

function CharCountBadge({ count, max }: { count: number; max: number }): React.ReactElement {
  const isOver = count > max;
  return (
    <Badge
      variant={isOver ? 'danger-muted' : 'default'}
      size="xs"
      leftIcon={isOver ? <AlertCircle size={10} /> : undefined}
      className={isOver ? 'animate-pulse font-mono' : 'font-mono'}
    >
      {count}/{max}
    </Badge>
  );
}

interface ResultBlockProps {
  title: string;
  content: string;
  className?: string;
  icon?: React.ReactNode;
  maxChars?: number;
  variant?: 'default' | 'danger';
  /** Forgejo #106 -- when provided (and editingEnabled), renders an Edit
   *  affordance that swaps the block into a textarea, and persists via this
   *  callback on Save. Omit to keep the block read-only (unchanged behavior). */
  onSave?: (value: string) => Promise<void>;
  editingEnabled?: boolean;
}

function ResultBlock({ title, content, className, icon, maxChars, variant = 'default', onSave, editingEnabled }: ResultBlockProps): React.ReactElement {
  const { addToast } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [editedValue, setEditedValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const displayContent = isEditing ? editedValue : (content || '');
  const charCount = displayContent.length;
  const overLimit = !!maxChars && isEditing && charCount > maxChars;
  const accentColor = variant === 'danger' ? '#ef4444' : 'rgb(var(--color-accent))';
  const borderColor = variant === 'danger' ? 'border-red-500/30' : 'border-slate-700';
  const bgColor = variant === 'danger' ? 'bg-red-900/10' : 'bg-slate-800/40';
  const canEdit = !!onSave && editingEnabled;

  async function handleSave(): Promise<void> {
    if (!onSave) return;
    if (overLimit) {
      addToast({ message: `Over the ${maxChars}-character limit -- trim before saving.`, variant: 'error' });
      return;
    }
    setIsSaving(true);
    try {
      await onSave(editedValue);
      setIsEditing(false);
      addToast({ message: `${title} saved`, variant: 'success' });
    } catch (err) {
      addToast({ message: err instanceof Error ? err.message : 'Save failed', variant: 'error' });
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className={`${bgColor} border ${isEditing ? 'border-amber-600/50' : borderColor} rounded-xl transition-all hover:bg-slate-800/60 ${className}`}>
      <div className="p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs font-black uppercase tracking-widest flex items-center gap-2" style={{ color: accentColor }}>
            {icon}
            {title}
          </h3>
          <div className="flex items-center gap-2">
            {maxChars && <CharCountBadge count={charCount} max={maxChars} />}
            {canEdit && !isEditing && (
              <Tooltip text={`Edit ${title}`}>
                <IconButton
                  aria-label={`Edit ${title}`}
                  size="xs"
                  variant="ghost"
                  onClick={() => { setEditedValue(content || ''); setIsEditing(true); }}
                  className="bg-slate-700/30 text-slate-400 border border-slate-600/30 hover:bg-slate-700/50"
                >
                  <Pencil size={12} />
                </IconButton>
              </Tooltip>
            )}
          </div>
        </div>
        {isEditing ? (
          <div className="space-y-2">
            <textarea
              value={editedValue}
              onChange={(e) => setEditedValue(e.target.value)}
              className="w-full bg-slate-950 border border-amber-600/50 rounded-lg p-3 text-sm text-slate-200 leading-relaxed resize-y min-h-[100px] focus:outline-none focus:border-amber-500"
              rows={5}
              autoFocus
              disabled={isSaving}
            />
            <div className="flex items-center justify-end gap-2">
              <Button variant="secondary" size="sm" leftIcon={<X size={12} />} onClick={() => setIsEditing(false)} disabled={isSaving}>
                Cancel
              </Button>
              <Button variant="primary" size="sm" leftIcon={<Save size={12} />} onClick={handleSave} isLoading={isSaving} disabled={overLimit}>
                Save
              </Button>
            </div>
          </div>
        ) : (
          // Forgejo #106 follow-up: the Copy overlay used to be absolutely
          // positioned against the WHOLE card, which put it directly on top
          // of the Edit button in the header above -- unclickable underneath
          // it. Scoped to its own relative wrapper around just the paragraph
          // (below the header, not overlapping it), same as the Lyrics panel
          // below already does correctly.
          <div className="relative group/copy">
            <p className={`text-sm ${variant === 'danger' ? 'text-red-300' : 'text-slate-200'} whitespace-pre-wrap leading-relaxed pr-8`}>
              {content || 'N/A'}
            </p>
            <div className="absolute top-0 right-0 opacity-0 group-hover/copy:opacity-100 transition-opacity">
              <CopyButton textToCopy={content} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function OutputSection({
    output,
    blueprint,
    critique,
    isSyncing,
    onSync,
    onApplyRevision,
    onLyricsUpdate,
    onFieldUpdate,
    editingEnabled,
    isSunoReady,
}: OutputSectionProps): React.ReactElement {
  const { openCompare, setSlotA } = useCompare();
  const { createSlotFromOutput } = useCreateCompareSlot();
  const { addToast } = useToast();
  const [showPronunciationModal, setShowPronunciationModal] = useState(false);
  const [pronunciationResult, setPronunciationResult] = useState<ReplacementResult | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedLyrics, setEditedLyrics] = useState('');
  const [isSavingLyrics, setIsSavingLyrics] = useState(false);

  function makeFieldSaver(field: EditableOutputField): ((value: string) => Promise<void>) | undefined {
    if (!onFieldUpdate) return undefined;
    return (value: string) => onFieldUpdate(field, value);
  }

  function handleCompare(): void {
    const slot = createSlotFromOutput(output);
    setSlotA(slot);
    openCompare();
  }

  function handleScanPronunciation(): void {
    const result = scanForPronunciationIssues(output.lyricsWithTags);
    setPronunciationResult(result);
    setShowPronunciationModal(true);
  }

  async function handleApplyPronunciationFixes(fixedLyrics: string): Promise<void> {
    if (!onLyricsUpdate) return;
    try {
      await onLyricsUpdate(fixedLyrics);
      addToast({ message: 'Pronunciation fixes applied!', variant: 'success' });
    } catch (err) {
      addToast({ message: err instanceof Error ? err.message : 'Failed to save pronunciation fixes', variant: 'error' });
    }
  }

  function handleDownloadMarkdown(): void {
    let markdownContent = `
# ${output.titleArtist}

## Summary
${output.summary}

## Style (Production Blueprint)
${output.styleBlueprint}

## Suno Tags
\`\`\`
${output.style}
\`\`\`

## Excluded Styles
\`\`\`
${output.excludedStyle}
\`\`\`

## Lyrics
\`\`\`markdown
${output.lyricsWithTags}
\`\`\`

## Suno Console JavaScript
\`\`\`javascript
${output.javascriptCode}
\`\`\`
`.trim();

    if (blueprint) {
      markdownContent += `\n\n## Art Image Blueprint (JSON)\n\`\`\`json\n${blueprint}\n\`\`\``;
    }

    if (critique) {
      markdownContent += `\n\n## Big Chuck's Critique\n`;
      markdownContent += `### Overall Score: ${critique.overallScore ?? 0}/10\n`;
      markdownContent += `**Verdict:** ${critique.summaryVerdict || 'N/A'}\n\n`;

      markdownContent += `#### Lyrical Analysis (${critique.sections?.lyricalAnalysis?.score ?? 0}/10)\n`;
      markdownContent += `${critique.sections?.lyricalAnalysis?.feedback || 'N/A'}\n`;
      markdownContent += `*Strengths:* ${(critique.sections?.lyricalAnalysis?.strengths || []).join(', ')}\n`;
      markdownContent += `*Weaknesses:* ${(critique.sections?.lyricalAnalysis?.weaknesses || []).join(', ')}\n\n`;

      markdownContent += `#### Musical Arrangement (${critique.sections?.musicalArrangement?.score ?? 0}/10)\n`;
      markdownContent += `${critique.sections?.musicalArrangement?.feedback || 'N/A'}\n`;
      markdownContent += `*Instrumentation:* ${critique.sections?.musicalArrangement?.instrumentationReview || 'N/A'}\n`;
      markdownContent += `*Groove:* ${critique.sections?.musicalArrangement?.grooveAnalysis || 'N/A'}\n\n`;

      markdownContent += `#### Production Quality (${critique.sections?.production?.score ?? 0}/10)\n`;
      markdownContent += `${critique.sections?.production?.feedback || 'N/A'}\n`;

      markdownContent += `#### Genre Authenticity (${critique.sections?.genreAuthenticity?.score ?? 0}/10)\n`;
      markdownContent += `${critique.sections?.genreAuthenticity?.feedback || 'N/A'}\n`;
      markdownContent += `*Authentic Elements:* ${(critique.sections?.genreAuthenticity?.southernRockElements || []).join(', ')}\n\n`;

      markdownContent += `#### Recommendations for Improvement\n`;
      (critique.improvementSuggestions || []).forEach((s, i) => {
        markdownContent += `${i + 1}. ${s}\n`;
      });
    }

    const date = new Date().toISOString().split('T')[0];
    const sanitizedTitle = (output.title ?? '').replace(/[^a-z0-9]/gi, '_').toLowerCase();
    const filename = `HookHouse-Pro-LLM-${sanitizedTitle}-${date}.md`;

    const blob = new Blob([markdownContent], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  const versionMatch = (output.title ?? '').match(/\[V(\d+)\]/);
  const nextVersionNum = versionMatch ? parseInt(versionMatch[1]) + 1 : 1;
  const versionLabel = `[V${nextVersionNum}]`;

  return (
    <div className="space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-500">

      <div className="flex flex-col xl:flex-row justify-between items-start gap-6 border-b border-slate-800 pb-8">
        <div>
           <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-accent rounded text-slate-950">
                <Music size={20} />
              </div>
              <h2 className="text-4xl font-black text-slate-50 tracking-tighter uppercase">{output.title}</h2>
           </div>
           <p className="text-slate-400 font-mono text-xs uppercase tracking-widest ml-11">
             Artist: {output.artist} | Blueprint Generated Successfully
             {output.llmModel && (() => {
               const p = output.llmProvider || '';
               const colors = p === 'claude' ? 'text-orange-400 bg-orange-500/10 border-orange-500/30 shadow-orange-500/20'
                 : p === 'openai' ? 'text-green-400 bg-green-500/10 border-green-500/30 shadow-green-500/20'
                 : p === 'xai' ? 'text-red-400 bg-red-500/10 border-red-500/30 shadow-red-500/20'
                 : 'text-blue-400 bg-blue-500/10 border-blue-500/30 shadow-blue-500/20';
               return (
                 <span className={`ml-2 inline-flex items-center text-[10px] font-bold px-2 py-0.5 rounded-full border shadow-sm ${colors}`}>
                   {p.toUpperCase()} · {output.llmModel}
                 </span>
               );
             })()}
           </p>
        </div>

        <div className="flex items-center gap-3 flex-wrap xl:flex-nowrap shrink-0">
          <Tooltip text="Open A/B Comparison View">
            <Button
              variant="secondary"
              size="md"
              leftIcon={<Scale size={18} />}
              onClick={handleCompare}
              className="shadow-lg"
            >
              <span className="text-sm font-bold uppercase tracking-tight">Compare</span>
            </Button>
          </Tooltip>
          {critique && onApplyRevision && (
            <Tooltip text={`Regenerate song applying Big Chuck's advice to create ${versionLabel}`}>
              <Button
                variant="primary"
                size="md"
                leftIcon={<Sparkles size={18} />}
                onClick={onApplyRevision}
                className="shadow-xl shadow-amber-900/20"
              >
                <span className="text-sm font-black uppercase tracking-tight">Apply {versionLabel}</span>
              </Button>
            </Tooltip>
          )}
          <Tooltip text="Download full session .md">
            <Button
              variant="secondary"
              size="md"
              leftIcon={<Download size={18} />}
              onClick={handleDownloadMarkdown}
              className="shadow-lg"
            >
              <span className="text-sm font-bold uppercase tracking-tight">Export .md</span>
            </Button>
          </Tooltip>
          <Button
            variant="primary"
            size="lg"
            leftIcon={isSyncing ? <LoadingSpinner size="sm" /> : <Zap size={18} />}
            onClick={onSync}
            disabled={isSyncing || !isSunoReady}
            isLoading={isSyncing}
            loadingText="Syncing..."
            className="shadow-xl shadow-amber-900/20 uppercase tracking-tighter"
          >
            Direct Sync
          </Button>
        </div>
      </div>

      {/* Over-limit warning */}
      {output.charLimits && (output.charLimits.styleTags.over || output.charLimits.excludedStyles.over || output.charLimits.lyrics.over) && (
        <div className="flex items-start gap-3 p-4 bg-red-950/30 border border-red-500/30 rounded-xl animate-in fade-in duration-300">
          <AlertCircle size={18} className="text-red-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-black text-red-400 uppercase tracking-widest mb-1">Suno Character Limits Exceeded</p>
            <div className="flex flex-wrap gap-3 text-[11px] text-red-300/80">
              {output.charLimits.styleTags.over && (
                <span>Tags: <strong className="text-red-300">{output.charLimits.styleTags.count.toLocaleString()}</strong>/{output.charLimits.styleTags.max.toLocaleString()}</span>
              )}
              {output.charLimits.excludedStyles.over && (
                <span>Excluded: <strong className="text-red-300">{output.charLimits.excludedStyles.count.toLocaleString()}</strong>/{output.charLimits.excludedStyles.max.toLocaleString()}</span>
              )}
              {output.charLimits.lyrics.over && (
                <span>Lyrics: <strong className="text-red-300">{output.charLimits.lyrics.count.toLocaleString()}</strong>/{output.charLimits.lyrics.max.toLocaleString()}</span>
              )}
            </div>
            <p className="text-[10px] text-slate-500 mt-1">Console injection will auto-truncate to Suno limits. Edit manually or regenerate for best results.</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        <ResultBlock
          title="Narrative Summary"
          content={output.summary}
          className="md:col-span-12"
          onSave={makeFieldSaver('summary')}
          editingEnabled={editingEnabled}
        />

        <div className="md:col-span-5 grid grid-cols-1 gap-6">
          <ResultBlock
            title="Machine Tags"
            content={output.style}
            icon={<Tags size={16}/>}
            maxChars={1000}
            onSave={makeFieldSaver('style')}
            editingEnabled={editingEnabled}
          />
          <ResultBlock
            title="Excluded Styles"
            content={output.excludedStyle}
            icon={<Ban size={16}/>}
            variant="danger"
            maxChars={1000}
            onSave={makeFieldSaver('excludedStyle')}
            editingEnabled={editingEnabled}
          />
        </div>

        <ResultBlock
          title="Production Blueprint"
          content={output.styleBlueprint}
          icon={<Sparkles size={16}/>}
          className="md:col-span-7"
          onSave={makeFieldSaver('styleBlueprint')}
          editingEnabled={editingEnabled}
        />
      </div>

      <div className="flex flex-wrap gap-4 px-1">
        {blueprint && (
          <Badge
            variant="info-muted"
            size="md"
            leftIcon={<StatusDot color="info" pulse />}
            className="tracking-[0.2em] font-bold uppercase"
          >
            Scene Architecture Ready
          </Badge>
        )}
        {critique && (
          <Badge
            variant="success-muted"
            size="md"
            leftIcon={<StatusDot color="success" pulse />}
            className="tracking-[0.2em] font-bold uppercase"
          >
            Expert Critique Loaded
          </Badge>
        )}
        <a href="https://suno.com/create" target="_blank" rel="noopener noreferrer">
          <Badge
            variant="default"
            size="md"
            leftIcon={<ExternalLink size={14} />}
            className="tracking-[0.2em] font-bold uppercase hover:text-slate-50 transition-colors cursor-pointer"
          >
            Open Suno.ai
          </Badge>
        </a>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-10">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-black text-accent uppercase tracking-[0.2em] flex items-center gap-3"><Music size={18}/> Lyrical Engine Output</h3>
            <div className="flex items-center gap-2">
              {!isEditing && editingEnabled && (
                <Tooltip text="Edit lyrics inline">
                  <Button
                    variant="ghost"
                    size="sm"
                    leftIcon={<Pencil size={14} />}
                    onClick={() => { setEditedLyrics(output.lyricsWithTags); setIsEditing(true); }}
                    className="bg-slate-700/30 text-slate-400 border border-slate-600/30 hover:bg-slate-700/50 uppercase tracking-wider"
                  >
                    Edit
                  </Button>
                </Tooltip>
              )}
              <Tooltip text="Scan for common Suno pronunciation issues and apply fixes">
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<Zap size={14} />}
                  onClick={handleScanPronunciation}
                  className="bg-purple-500/20 text-purple-400 border border-purple-500/30 hover:bg-purple-500/30 uppercase tracking-wider"
                >
                  Fix Pronunciation
                </Button>
              </Tooltip>
              <CharCountBadge count={isEditing ? editedLyrics.length : (output.lyricsWithTags ?? '').length} max={5000} />
            </div>
          </div>
          {isEditing ? (
            <div className="space-y-3">
              <textarea
                value={editedLyrics}
                onChange={(e) => setEditedLyrics(e.target.value)}
                className="w-full bg-slate-950 border-2 border-amber-600/50 rounded-2xl p-6 text-[13px] text-slate-300 font-mono whitespace-pre-wrap leading-relaxed max-h-[600px] resize-none focus:outline-none focus:border-amber-500"
                rows={20}
              />
              <div className="flex items-center justify-end gap-2">
                <Button variant="secondary" size="sm" leftIcon={<X size={14} />} onClick={() => setIsEditing(false)} disabled={isSavingLyrics}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  leftIcon={<Save size={14} />}
                  isLoading={isSavingLyrics}
                  onClick={async () => {
                    if (!onLyricsUpdate) return;
                    setIsSavingLyrics(true);
                    try {
                      await onLyricsUpdate(editedLyrics);
                      setIsEditing(false);
                      addToast({ message: 'Lyrics saved', variant: 'success' });
                    } catch (err) {
                      addToast({ message: err instanceof Error ? err.message : 'Save failed', variant: 'error' });
                    } finally {
                      setIsSavingLyrics(false);
                    }
                  }}
                >
                  Save
                </Button>
              </div>
            </div>
          ) : (
            <div className="bg-slate-950 border-2 border-slate-800 rounded-2xl relative shadow-inner">
              <pre className="p-6 text-[13px] text-slate-300 font-mono whitespace-pre-wrap leading-relaxed max-h-[600px] overflow-y-auto custom-scrollbar">{output.lyricsWithTags}</pre>
              <div className="absolute top-4 right-4">
                 <CopyButton textToCopy={output.lyricsWithTags} className="!bg-slate-800/80 !backdrop-blur" />
              </div>
            </div>
          )}
        </div>

        <div className="space-y-8">
           <div className="space-y-4">
              <h3 className="text-sm font-black text-accent uppercase tracking-[0.2em] flex items-center gap-3"><Code size={18}/> Automation Terminal</h3>
              <div className="bg-black border-2 border-slate-800 rounded-2xl relative group shadow-inner">
                <pre className="p-6 text-[11px] text-green-500 font-mono whitespace-pre-wrap leading-relaxed max-h-60 overflow-y-auto custom-scrollbar">{output.javascriptCode}</pre>
                <div className="absolute top-4 right-4">
                  <CopyButton textToCopy={output.javascriptCode} className="!bg-slate-800/80 !backdrop-blur" />
                </div>
                <div className="absolute inset-x-0 bottom-0 p-3 bg-slate-900/80 border-t border-slate-800 backdrop-blur-sm rounded-b-2xl">
                   <p className="text-[11px] text-slate-400 text-center uppercase tracking-widest font-bold">Paste into Suno Console while in "Custom Mode"</p>
                </div>
              </div>
           </div>

           <div className="space-y-4">
              <h3 className="text-sm font-black text-accent uppercase tracking-[0.2em] flex items-center gap-3"><FileText size={18}/> Narrative Content</h3>
              <div className="bg-slate-800/20 border-2 border-slate-800 rounded-2xl relative">
                <pre className="p-6 text-[13px] text-slate-400 font-mono whitespace-pre-wrap leading-relaxed max-h-80 overflow-y-auto custom-scrollbar">{output.lyricsAlone}</pre>
                <div className="absolute top-4 right-4">
                   <CopyButton textToCopy={output.lyricsAlone} className="!bg-slate-700/80 !backdrop-blur" />
                </div>
              </div>
           </div>
        </div>
      </div>

      {/* Pronunciation Fix Modal */}
      <PronunciationFixModal
        isOpen={showPronunciationModal}
        onClose={() => setShowPronunciationModal(false)}
        result={pronunciationResult}
        onApply={handleApplyPronunciationFixes}
        onRefresh={handleScanPronunciation}
      />

    </div>
  );
}
